/**
 * Core domain enumerations for the photography booking site.
 *
 * These enums are shared between the client (SPA) and server (Lambda handlers)
 * to guarantee a single source of truth for status values that cross the
 * public/owner boundaries.
 */
/**
 * The lifecycle state of a {@link BookingInquiry}.
 *
 * Valid transitions (enforced by the booking state machine):
 *   PENDING -> APPROVED | DENIED | EXPIRED
 * Terminal states (APPROVED, DENIED, EXPIRED) never transition further.
 */
export const BookingStatus = {
    PENDING: "PENDING",
    APPROVED: "APPROVED",
    DENIED: "DENIED",
    EXPIRED: "EXPIRED",
};
/**
 * The kind of shoot a Visitor is requesting. Kept deliberately small: the model
 * offers swimwear and fashion shoots; anything else is "Other" (the requester
 * describes it in their message, and the model follows up). Used to label the
 * booking and compose the calendar event title on approval.
 */
export const ShootType = {
    SWIM: "SWIM",
    FASHION: "FASHION",
    OTHER: "OTHER",
};
/** Human-readable labels for each {@link ShootType}. */
export const SHOOT_TYPE_LABELS = {
    SWIM: "Swim",
    FASHION: "Fashion",
    OTHER: "Other",
};
/**
 * The only availability values ever exposed to a Visitor.
 *
 * Personal calendar detail (event titles, reasons, connection identifiers)
 * never crosses the public boundary - the public surface collapses to exactly
 * one of these two values.
 */
export const AvailabilityStatus = {
    AVAILABLE: "AVAILABLE",
    UNAVAILABLE: "UNAVAILABLE",
};
/**
 * Supported external calendar providers.
 *
 * Each provider is integrated behind the CalendarProvider interface via a
 * dedicated adapter (Google Calendar API, CalDAV/iCloud, Microsoft Graph).
 */
export const CalendarProvider = {
    GOOGLE: "GOOGLE",
    ICLOUD: "ICLOUD",
    OUTLOOK: "OUTLOOK",
};
/**
 * The lifecycle state of a calendar connection.
 *
 * A connection contributes busy dates to availability while it is in any state
 * other than fully disconnected: ACTIVE, ERROR, and DISCONNECTING all still
 * affect availability until disconnection completes.
 */
export const ConnectionStatus = {
    ACTIVE: "ACTIVE",
    ERROR: "ERROR",
    DISCONNECTING: "DISCONNECTING",
};
/**
 * Why a transactional email was recorded as a delivery failure for Owner
 * visibility (Req 10.4).
 *
 *   - RETRY_EXHAUSTED — the send was retried per the configured retry policy
 *     and still could not be accepted for delivery;
 *   - BOUNCE — SES reported the message bounced (hard or soft);
 *   - COMPLAINT — the recipient reported the message as spam.
 */
export const DeliveryFailureKind = {
    RETRY_EXHAUSTED: "RETRY_EXHAUSTED",
    BOUNCE: "BOUNCE",
    COMPLAINT: "COMPLAINT",
};
/** Lifecycle state of a portfolio image. */
export const ImageStatus = {
    PENDING_RENDITION: "PENDING_RENDITION",
    PUBLISHED: "PUBLISHED",
};
/**
 * Connection statuses that still contribute busy dates to availability.
 * (Everything except fully disconnected.)
 */
export const AVAILABILITY_AFFECTING_CONNECTION_STATUSES = [ConnectionStatus.ACTIVE, ConnectionStatus.ERROR, ConnectionStatus.DISCONNECTING];
//# sourceMappingURL=enums.js.map